const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/components/PDFViewer/pdfZoomUtils.ts';
export const MIN_PDF_SCALE_PERCENT = 50;
export const MAX_PDF_SCALE_PERCENT = 250;

type ClientPoint = {
    clientX: number;
    clientY: number;
};

export type RectLike = {
    left: number;
    top: number;
    right: number;
    bottom: number;
    width: number;
    height: number;
};

export type PageRect = {
    pageNumber: number;
    rect: RectLike;
};

export type ZoomAnchor = {
    focalX: number;
    focalY: number;
    pageNumber: number;
    pageXRatio: number;
    pageYRatio?: number;
    pageYEdge?: 'top' | 'bottom';
    pageYEdgeOffset?: number;
};

// Clamps scale to the allowed zoom range [MIN_PDF_SCALE_PERCENT, MAX_PDF_SCALE_PERCENT].
export const clampPDFScale = (scale: number) => Math.min(Math.max(scale, MIN_PDF_SCALE_PERCENT), MAX_PDF_SCALE_PERCENT);

export const getTouchMidpoint = (touches: ArrayLike<ClientPoint>) => ({
    clientX: (touches[0].clientX + touches[1].clientX) / 2,
    clientY: (touches[0].clientY + touches[1].clientY) / 2,
});

// Euclidean distance between two touch points.
export const getTouchDistance = (touches: ArrayLike<ClientPoint>): number => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
};

// The CSS preview scale is relative to the current PDF scale, so its bounds must be too.
export const getPreviewScaleBounds = (currentScale: number) => ({
    minPreviewScale: MIN_PDF_SCALE_PERCENT / currentScale,
    maxPreviewScale: MAX_PDF_SCALE_PERCENT / currentScale,
});

export const getCommittedScale = (currentScale: number, previewScale: number) =>
    clampPDFScale(Math.round(currentScale * previewScale * 10) / 10);

export const getClosestPage = (pages: PageRect[], focalClientY: number) => {
    const sortedPages = [...pages].sort((a, b) => a.pageNumber - b.pageNumber);

    const pageAtPoint = sortedPages.find(page => focalClientY >= page.rect.top && focalClientY <= page.rect.bottom);
    if (pageAtPoint) return pageAtPoint;

    // If the pinch midpoint is in the fixed gap between pages, anchor to the nearest page edge.
    return sortedPages.reduce<PageRect | null>((closest, page) => {
        const distance = Math.min(Math.abs(focalClientY - page.rect.top), Math.abs(focalClientY - page.rect.bottom));

        if (!closest) return page;

        const closestDistance = Math.min(
            Math.abs(focalClientY - closest.rect.top),
            Math.abs(focalClientY - closest.rect.bottom)
        );
        return distance < closestDistance ? page : closest;
    }, null);
};

// Inverts the live wrapper transform to recover the wrapper-local coords under a client point.
// During a pinch, the wrapper carries `transform-origin: 0 0; transform: translate(tx, ty) scale(s)`,
// so a wrapper-local point (lx, ly) appears at client (originLeft + tx + lx*s, originTop + ty + ly*s).
export const mapClientPointToWrapperLocal = (
    clientPoint: { clientX: number; clientY: number },
    wrapperOrigin: { left: number; top: number },
    pan: { tx: number; ty: number },
    previewScale: number
): { x: number; y: number } => {
    const safeScale = previewScale === 0 ? 1 : previewScale;
    return {
        x: (clientPoint.clientX - wrapperOrigin.left - pan.tx) / safeScale,
        y: (clientPoint.clientY - wrapperOrigin.top - pan.ty) / safeScale,
    };
};

// Builds an anchor from a wrapper-local point. The pages list is in wrapper-local coords
// (captured at PINCHING entry, before the transform was applied). The focal target stored
// on the returned anchor is the CLIENT position where the anchor should land after the
// post-commit re-render (i.e. wherever the user's fingers lifted from).
export const buildWrapperLocalAnchor = (
    pages: PageRect[],
    local: { x: number; y: number },
    focalClient: { clientX: number; clientY: number }
): ZoomAnchor | null => {
    const focalPage = getClosestPage(pages, local.y);
    if (!focalPage) return null;

    const pageRect = focalPage.rect;
    const pageYRatio = (local.y - pageRect.top) / Math.max(pageRect.height, 1);

    const verticalAnchor =
        local.y < pageRect.top
            ? { pageYEdge: 'top' as const, pageYEdgeOffset: local.y - pageRect.top }
            : local.y > pageRect.bottom
              ? { pageYEdge: 'bottom' as const, pageYEdgeOffset: local.y - pageRect.bottom }
              : { pageYRatio };

    return {
        focalX: focalClient.clientX,
        focalY: focalClient.clientY,
        pageNumber: focalPage.pageNumber,
        pageXRatio: (local.x - pageRect.left) / Math.max(pageRect.width, 1),
        ...verticalAnchor,
    };
};

// Returns the position of an anchor relative to a given page rect, in whatever coord system
// pageRect is in (client coords if pageRect is a getBoundingClientRect result, etc).
export const getAnchorPosOnPage = (anchor: ZoomAnchor, pageRect: RectLike): { x: number; y: number } => {
    const x = pageRect.left + anchor.pageXRatio * pageRect.width;
    const y =
        anchor.pageYEdge === 'top'
            ? pageRect.top + (anchor.pageYEdgeOffset ?? 0)
            : anchor.pageYEdge === 'bottom'
              ? pageRect.bottom + (anchor.pageYEdgeOffset ?? 0)
              : pageRect.top + (anchor.pageYRatio ?? 0) * pageRect.height;
    return { x, y };
};
